import { Loader2, Smile, Frown } from 'lucide-react'
import {
  ANXIETY_BANDS,
  RATING_LABELS,
  RATING_MAX,
  RATING_MIN,
  getRatingButtonColorDynamic,
  type AnxietyBandConfig,
} from '@drive-without-fear/core/config/anxiety-rating'

export type AnxietyRatingVariant = 'screen' | 'card'

interface AnxietyRatingScaleProps {
  value: number | null
  onChange: (rating: number) => void
  onSubmit: () => void | Promise<void>
  isSubmitting?: boolean
  variant?: AnxietyRatingVariant
  title?: string
  description?: string
  submitLabel?: string
  /**
   * Anxiety bands shown alongside the scale. Defaults to the static
   * 0–3 / 4–5 / 6–8 split. Pass derived bands (via `buildBandsFromThresholds`
   * + `useThresholds`) to keep the UI aligned with admin-configured thresholds.
   */
  bands?: readonly AnxietyBandConfig[]
}

const RATINGS: readonly number[] = Array.from(
  { length: RATING_MAX - RATING_MIN + 1 },
  (_, i) => RATING_MIN + i,
)

/**
 * Shared anxiety rating UI for the web app. Renders the 0–8 scale,
 * Low/Medium/High band labels, anchor icons, and the submit button.
 *
 * Variant 'screen' renders a standalone full-width card (free assessment).
 * Variant 'card' renders a compact card suitable for modal embedding (paid programme).
 *
 * The component is controlled — pass `value` and `onChange` to manage state externally.
 */
export function AnxietyRatingScale({
  value,
  onChange,
  onSubmit,
  isSubmitting = false,
  variant = 'screen',
  title = 'How anxious do you feel?',
  description = 'Rate your anxiety level from 0 (no anxiety) to 8 (extreme anxiety)',
  submitLabel = 'Submit Rating',
  bands = ANXIETY_BANDS,
}: AnxietyRatingScaleProps) {
  const isDisabled = value === null || isSubmitting

  const containerClass =
    variant === 'screen'
      ? 'bg-[#FBF9F5] rounded-[24px] shadow-[0_6px_18px_rgba(15,28,46,0.08)] p-6 max-w-lg mx-auto'
      // max-w-lg (512px) instead of max-w-md (448px) so the 9-button 0–8 row
      // fits on a single line without wrapping in the paid-programme modal.
      : 'bg-white rounded-[16px] shadow-[0_2px_4px_rgba(0,0,0,0.1)] p-6 max-w-lg mx-auto'

  return (
    <div className={containerClass}>
      <h3 className="text-[20px] font-semibold text-[#0F1C2E] mb-2 text-center">{title}</h3>
      <p className="text-[#6E7582] mb-5 text-center text-sm">{description}</p>

      {/* Band labels — only shown in the standalone 'screen' variant (free
          assessment). The compact 'card' variant (paid programme modal) skips
          them to keep the modal tight; the anchor icons + selected-rating
          label below convey the scale meaning in that compact context. */}
      {variant === 'screen' && (
        <div className="flex flex-wrap justify-center gap-2 mb-5">
          {bands.map((band) => (
            <span
              key={band.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F6F3EC] text-xs font-medium text-[#0F1C2E]"
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: band.color }}
              />
              {band.label}
              <span className="text-[#6E7582] font-normal">({band.rangeLabel})</span>
            </span>
          ))}
        </div>
      )}

      {/* Rating buttons */}
      <div className="mb-3">
        <div className="flex justify-between flex-wrap gap-2 mb-3">
          {RATINGS.map((rating) => {
            const isSelected = value === rating
            return (
              <button
                key={rating}
                type="button"
                onClick={() => onChange(rating)}
                disabled={isSubmitting}
                aria-label={`Rating ${rating}`}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  isSelected
                    ? 'text-white ring-2 ring-offset-2 ring-[#0F1C2E] scale-110'
                    : 'text-[#0F1C2E] hover:opacity-90'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{ backgroundColor: getRatingButtonColorDynamic(rating, isSelected, bands) }}
              >
                {rating}
              </button>
            )
          })}
        </div>

        {/* Anchor icons */}
        <div className="flex justify-between text-xs px-1 items-center">
          <span className="text-[#6B9D7A] font-medium flex items-center gap-1">
            <Smile size={14} />
            No anxiety
          </span>
          <span className="text-[#C7796B] font-medium flex items-center gap-1">
            Severe
            <Frown size={14} />
          </span>
        </div>
      </div>

      {/* Selected display */}
      {value !== null && (
        <div className="my-5 p-4 bg-[#F6F3EC] border border-[#E3DED2] rounded-[16px] text-center">
          <span className="text-2xl font-bold text-[#0F1C2E]">{value}</span>
          <span className="text-[#6E7582] ml-2">— {RATING_LABELS[value]}</span>
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={() => void onSubmit()}
        disabled={isDisabled}
        className="w-full py-3.5 bg-[#D4AF5A] text-[#0F1C2E] font-semibold rounded-[16px] hover:bg-[#c9a34f] transition-colors disabled:bg-[#A4A9B3] disabled:text-white disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(15,28,46,0.12)]"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Submitting...
          </>
        ) : (
          submitLabel
        )}
      </button>
    </div>
  )
}
